import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Music, Clock, ArrowRight } from "lucide-react";
import Layout from "@/components/layout/Layout";
import SectionHeading from "@/components/shared/SectionHeading";
import ContactButtons from "@/components/shared/ContactButtons";

const Funerals = () => {
  const services = [
    {
      icon: <Music className="w-8 h-8" />,
      title: "Service Music",
      description: "Gentle, moving performances during the service to honour your loved one and provide comfort to all present."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Entrance & Exit Music",
      description: "Setting a peaceful tone as guests arrive and providing a meaningful farewell at the close of the service."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Graveside or Wake",
      description: "Music for the committal or gathering afterwards, helping to create a space for reflection and remembrance."
    }
  ];

  const songSuggestions = [
    { title: "Ave Maria", artist: "Traditional" },
    { title: "The Lord is My Shepherd", artist: "Traditional" },
    { title: "Time to Say Goodbye", artist: "Sarah Brightman" },
    { title: "Pie Jesu", artist: "Andrew Lloyd Webber" },
    { title: "You Raise Me Up", artist: "Josh Groban" },
    { title: "Wind Beneath My Wings", artist: "Bette Midler" },
    { title: "Hallelujah", artist: "Leonard Cohen" },
    { title: "Amazing Grace", artist: "Traditional" }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-secondary">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full bg-gradient-to-b from-black/20 to-transparent" />
        </div>
        
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Heart className="w-10 h-10 text-primary mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-playfair font-semibold text-cream mb-6">
              Funeral & Memorial Services
            </h1>
            <p className="text-xl text-cream/80 font-light leading-relaxed">
              Providing comfort and peace through music during life's most difficult moments
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-6"
          >
            <p className="text-lg text-secondary/80 leading-relaxed">
              I understand how difficult this time is for you and your family. Music can bring great comfort, helping to honour your loved one and providing a moment of peace during the service.
            </p>
            <p className="text-lg text-secondary/80 leading-relaxed">
              I approach every funeral service with the utmost care, sensitivity, and professionalism. My role is to support you and help create a meaningful tribute, taking care of all the musical details so you can focus on what matters most.
            </p>
            <p className="text-lg text-secondary/80 leading-relaxed">
              Please don't hesitate to reach out—I'm here to help in any way I can, and there's no pressure or obligation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-4">
          <SectionHeading 
            title="How I Can Help"
            subtitle="Gentle, professional musical support for your service"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 bg-white rounded-lg shadow-sm"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 rounded-full text-secondary mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-playfair font-semibold text-secondary mb-3">
                  {service.title}
                </h3>
                <p className="text-secondary/70">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Song Suggestions */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <SectionHeading 
            title="Song Suggestions"
            subtitle="Some frequently requested songs for funeral services. I can also learn specific pieces if they hold special meaning for your family."
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-3"
          >
            {songSuggestions.map((song, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between p-4 bg-cream/50 rounded-lg"
              >
                <span className="font-medium text-secondary">{song.title}</span>
                <span className="text-secondary/60 text-sm">{song.artist}</span>
              </div>
            ))}
          </motion.div>
          
          <p className="text-center text-secondary/60 mt-6 text-sm">
            This is just a small selection. Please feel free to request any songs that were meaningful to your loved one.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-cream mb-4">
              I'm Here to Help
            </h2>
            <p className="text-lg text-cream/80 mb-8">
              If you'd like to discuss how I can support your service, please get in touch. There's no obligation, and I'll be happy to answer any questions.
            </p>
            
            <ContactButtons variant="light" className="mb-6" />
            
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-cream hover:text-primary transition-colors"
            >
              Or send an enquiry <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-cream/50 text-sm mt-6">
              You may find this helpful: <Link to="/journal/funeral-singer-birmingham" className="underline underline-offset-4 hover:text-primary">choosing a funeral singer in Birmingham</Link>.
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Funerals;
